import React from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { getTenantId } from 'utils/getTenantId'

interface NewInsightsPopupProps {
  onClose: () => void
  onViewInsights: () => void
  insightsCount?: number
}

export const NewInsightsPopup: React.FC<NewInsightsPopupProps> = ({
  onClose,
  onViewInsights,
  insightsCount = 0,
}) => {
  const history = useHistory()
  const userProfile = useSelector((state: RootState) => state.user?.userProfile)
  const tenantId = getTenantId() // Use the same utility function as other components
  const userRoles = userProfile?.role || []

  // Format insights count for display (e.g., 999+ for large numbers)
  const displayCount = insightsCount > 999 ? '999+' : insightsCount.toString()

  // Determine singular vs plural for "Insight" vs "Insights"
  const insightLabel = insightsCount === 1 ? 'New Insight' : 'New Insights'

  const handleViewInsights = () => {
    // Safety check: Ensure tenantId is available
    if (!tenantId) {
      alert('Unable to navigate to insights page. Please try refreshing the page.')
      return
    }

    // Role-based redirect logic
    const isBsoAdvisor = userRoles.includes('BSO_ADVISOR')
    const isBsoAdmin = userRoles.includes('BSO_ADMIN')
    const isAdmin = userRoles.includes('ADMIN') || userRoles.includes('Admin')

    if (isBsoAdvisor || isBsoAdmin || isAdmin) {
      // BSO side - redirect to Advisor Insights page
      history.push(`/advisor/${tenantId}/insights`)
    } else {
      // BO side - redirect to Business Owner Insights page
      history.push(`/owner-v2/${tenantId}/insights`)
    }

    onViewInsights()
    onClose()
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '560px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'none',
            border: 'none',
            fontSize: '28px',
            cursor: 'pointer',
            color: '#6B7280',
            lineHeight: 1,
            padding: '5px',
          }}
        >
          ✕
        </button>

        <div style={{ textAlign: 'center' }}>
          {/* Football Character Icon */}
          <div style={{ marginBottom: '24px' }}>
            <img
              src="/images/football-character.svg"
              alt="Football Character"
              style={{ width: '194px', height: '194px', margin: '0 auto' }}
            />
          </div>

          {/* Main Heading */}
          <h2
            style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1F2937',
              marginBottom: '16px',
              lineHeight: '1.2',
            }}
          >
            I'm so excited to share this with you!
          </h2>

          {/* Subtitle */}
          <p
            style={{
              fontSize: '16px',
              color: '#6B7280',
              marginBottom: '24px',
              lineHeight: '1.6',
              maxWidth: '450px',
              margin: '0 auto 24px',
            }}
          >
            I just finished analyzing your business and found some really exciting insights that I
            think you're going to love.
          </p>

          {/* Insights Count Badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#6366F1',
              color: 'white',
              padding: '12px 20px',
              borderRadius: '12px',
              marginBottom: '32px',
              fontWeight: '600',
              fontSize: '16px',
            }}
          >
            {/* Lightbulb Icon - Outlined */}
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            <span style={{ color: 'white' }}>
              {displayCount} {insightLabel}
            </span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
            {/* Show Me What You Found Button */}
            <button
              onClick={handleViewInsights}
              style={{
                width: '100%',
                padding: '14px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                backgroundColor: '#6366F1',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#5558E3')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#6366F1')}
            >
              Show me what you found!
            </button>

            {/* I'll Check Later Button */}
            <button
              onClick={onClose}
              style={{
                width: '100%',
                padding: '14px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#6366F1',
                backgroundColor: 'white',
                border: '2px solid #6366F1',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#F3F4F6'
                e.currentTarget.style.borderColor = '#5558E3'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'white'
                e.currentTarget.style.borderColor = '#6366F1'
              }}
            >
              I'll check later
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewInsightsPopup
